/**
 * One Piece TCG Data Utilities
 * Functions for loading, filtering, and manipulating One Piece card data
 */

import { OnePieceSet, OnePieceCard, isChaseCard } from '../types/onepiece';

/**
 * Transform master sets from raw data
 */
export function loadOnePieceSets(setsData: any[] = []): OnePieceSet[] {
  try {
    // Transform API data to our OnePieceSet format
    return setsData.map((set: any) => {
      const transformedSet = transformApiSetToOnePieceSet(set);
      // Use pre-calculated card count from JSON metadata
      transformedSet.totalCards = set.totalCards || 0;
      transformedSet.printedTotal = transformedSet.totalCards;
      return transformedSet;
    });
  } catch (error) {
    console.error('Error transforming One Piece sets:', error);
    return [];
  }
}

/**
 * Load all One Piece cards from local JSON
 */
/**
 * Transform API card data to OnePieceCard format
 */

/**
 * Transform API set data to OnePieceSet format
 */
function transformApiSetToOnePieceSet(apiSet: any): OnePieceSet {
  // API returns set_id (e.g., "OP-01") and set_name
  const setId = apiSet.set_id || '';
  const setNumber = parseInt(setId.replace(/[^0-9]/g, '') || '0');
  
  // Use release date from API if available
  const releaseDate = apiSet.release_date || new Date().toISOString().split('T')[0];
  
  // Determine era based on release date (Year-based)
  const releaseYear = new Date(releaseDate).getFullYear();
  let era = 'Recent';
  if (releaseYear <= 2022) era = 'Early';
  else if (releaseYear === 2023) era = 'Mid';
  else if (releaseYear >= 2024) era = 'Recent';
  
  // Determine value tier (can be enhanced based on market data)
  let valueTier = 'Standard';
  if (setNumber === 1 || setNumber === 2) valueTier = 'Premium Value';
  else if (setNumber <= 5) valueTier = 'High Value';
  else if (setNumber <=8) valueTier = 'Medium Value';
  
  // Determine series type
  let series = 'Booster';
  if (setId.startsWith('ST-')) series = 'Starter Deck';
  else if (setId.startsWith('EB-')) series = 'Extra Booster';
  else if (setId.startsWith('PRB-')) series = 'Premium Booster';
  
  return {
    id: setId,
    name: apiSet.set_name || '',
    series,
    printedTotal: 0, // We'll get this from card data
    totalCards: 0,
    releaseDate,
    images: {
      symbol: '', 
      logo: apiSet.booster_box_image || '', // Use booster box image
    },
    era,
    valueTier,
    owned: 0, // Will be populated from ownership data
    completion: 0, // Will be calculated
    description: apiSet.description,
    msrp: apiSet.msrp,
    marketPrice: apiSet.market_price,
    sealedItems: apiSet.sealedItems,
  };
}

/**
 * Transform API card data to OnePieceCard format
 */
/**
 * Transform API card data to OnePieceCard format
 * Standardized transformation used across the app
 */
export function transformApiCardToOnePieceCard(apiCard: any, setId?: string): OnePieceCard {
  const cardId = apiCard.card_image_id || apiCard.card_set_id || '';
  
  const card: OnePieceCard = {
    id: cardId,
    cardId: cardId,
    baseCardId: apiCard.card_set_id || '',
    setId: apiCard.set_id || setId || '',
    name: apiCard.card_name || 'Unknown',
    nameJp: apiCard.nameJp,
    justtcgName: apiCard.justtcg_name,
    justtcgId: apiCard.justtcg_id,
    setName: apiCard.set_name || '',
    number: apiCard.card_set_id || '',
    rarity: apiCard.rarity ? apiCard.rarity.trim() : 'C',
    cost: apiCard.card_cost ? parseInt(apiCard.card_cost) : undefined,
    attribute: apiCard.attribute,
    power: apiCard.card_power ? parseInt(apiCard.card_power) : undefined,
    counter: apiCard.counter_amount,
    color: apiCard.card_color ? (Array.isArray(apiCard.card_color) ? apiCard.card_color : apiCard.card_color.split('/').map((c: string) => c.trim())) : [],
    type: apiCard.sub_types ? (Array.isArray(apiCard.sub_types) ? apiCard.sub_types : apiCard.sub_types.split(' ')) : [],
    effect: apiCard.card_text,
    trigger: apiCard.trigger,
    category: (apiCard.card_type || 'Character').trim(),
    images: {
      small: apiCard.card_image ? apiCard.card_image.replace(/\/([^/]+)\.(jpg|png)$/i, '/thumbnail/$1.webp') : '',
      large: apiCard.card_image || '',
      smallJp: apiCard.card_image_jp_thumbnail || '',
      largeJp: apiCard.card_image_jp || '',
    },
    prices: {
      low: apiCard.inventory_price,
      mid: apiCard.inventory_price,
      high: apiCard.market_price,
      market: apiCard.market_price,
    },
  };
  
  // Mark as chase card based on rarity or shared logic
  card.isChaseCard = isChaseCard(card);
  
  return card;
}

/**
 * Get unique eras from sets
 */
export function getUniqueEras(sets: OnePieceSet[]): string[] {
  const eras = new Set(sets.map(set => set.era));
  return Array.from(eras).sort();
}

/**
 * Get unique series from sets
 */
export function getUniqueSeries(sets: OnePieceSet[]): string[] {
  const series = new Set(sets.map(set => set.series));
  return Array.from(series).sort();
}

/**
 * Filter and sort One Piece sets
 */
export function filterAndSortSets(
  sets: OnePieceSet[],
  searchTerm: string,
  filterEra: string,
  filterSeries: string,
  sortBy: string,
  sortDirection: 'asc' | 'desc',
  tabFilter?: number // 0=all, 1=in progress, 2=complete, 3=not started
): OnePieceSet[] {
  let filtered = [...sets];
  
  // Apply search filter
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(set => 
      set.name.toLowerCase().includes(term) ||
      set.series.toLowerCase().includes(term) ||
      set.id.toLowerCase().includes(term)
    );
  }
  
  // Apply era filter
  if (filterEra && filterEra !== 'all') {
    filtered = filtered.filter(set => set.era === filterEra);
  }
  
  // Apply series filter
  if (filterSeries && filterSeries !== 'all') {
    filtered = filtered.filter(set => set.series === filterSeries);
  }
  
  // Apply tab filter
  if (tabFilter !== undefined) {
    switch (tabFilter) {
      case 1: // In Progress
        filtered = filtered.filter(set => set.completion > 0 && set.completion < 100);
        break;
      case 2: // Complete
        filtered = filtered.filter(set => set.completion === 100);
        break;
      case 3: // Not Started
        filtered = filtered.filter(set => set.completion === 0);
        break;
      default: // All
        break;
    }
  }
  
  // Sort
  filtered.sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'releaseDate':
        comparison = new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
        break;
      case 'completion':
        comparison = a.completion - b.completion;
        break;
      case 'series':
        comparison = a.series.localeCompare(b.series);
        break;
      default:
        comparison = new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  
  return filtered;
}

/**
 * Filter cards by various criteria
 */
export function filterCards(
  cards: OnePieceCard[],
  filters: {
    searchTerm?: string;
    colors?: string[];
    categories?: string[];
    rarities?: string[];
    chaseOnly?: boolean;
  }
): OnePieceCard[] {
  let filtered = [...cards];
  
  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(card =>
      card.name.toLowerCase().includes(term) ||
      (card.justtcgName && card.justtcgName.toLowerCase().includes(term)) ||
      card.number.toLowerCase().includes(term) ||
      card.type.some(t => t.toLowerCase().includes(term)) ||
      (card.category && card.category.toLowerCase().includes(term)) ||
      (card.rarity && card.rarity.toLowerCase() === term)
    );
  }
  
  if (filters.colors && filters.colors.length > 0) {
    filtered = filtered.filter(card =>
      card.color.some(c => filters.colors && filters.colors.includes(c))
    );
  }
  
  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter(card =>
      card.category && filters.categories && filters.categories.includes(card.category)
    );
  }
  
  if (filters.rarities && filters.rarities.length > 0) {
    filtered = filtered.filter(card =>
      filters.rarities!.includes(card.rarity)
    );
  }
  
  if (filters.chaseOnly) {
    filtered = filtered.filter(card => card.isChaseCard);
  }
  
  return filtered;
}

/**
 * Get chase cards from a set
 */
export function getChaseCards(cards: OnePieceCard[]): OnePieceCard[] {
  return cards.filter(card => card.isChaseCard);
}

/**
 * Find a card in a list of raw API cards or transformed cards
 * Prioritizes card_image_id -> card_set_id -> id
 */
export function findOnePieceCard(cards: any[], searchId: string): any {
  if (!cards || cards.length === 0) return undefined;
  
  // 1. Try exact match on card_image_id (most specific)
  let found = cards.find((c) => c.card_image_id === searchId);
  
  // 2. Try match on card_set_id (fallback)
  if (!found) {
    found = cards.find((c) => c.card_set_id === searchId);
  }
  
  // 3. Try match on id (if already transformed)
  if (!found) {
    found = cards.find((c) => c.id === searchId);
  }

  // 4. Try stripping suffix (for base card lookup if variant not found)
  if (!found && searchId.includes('_')) {
    const baseId = searchId.split('_')[0]; // Simple split for now
    found = cards.find((c) => c.card_image_id === baseId || c.card_set_id === baseId);
  }

  // 5. Try fuzzy lookup? No, let's keep it strict for now.
  
  return found;
}
